import Picker from "@/components/picker/mobile-picker";
import { PickerDivider } from "@/components/picker/mobile-picker/picker-divider";
import { FilledButton } from "@/components/ui/buttons/FilledButton";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { format } from "date-fns";
import { memo, useEffect, useMemo, useRef, useState } from "react";

interface DateSelectModalProps {
  defaultValue: string;
  onSelect: (value: string) => void;
}

const monthLabels = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const buildRange = (start: number, end: number) =>
  Array.from({ length: end - start + 1 }, (_, idx) => start + idx);

export const DateSelectModal = memo(function DateSelectModal({
  defaultValue,
  onSelect,
}: DateSelectModalProps) {
  const parsedDate = useMemo(() => {
    const date = new Date(defaultValue);
    if (isNaN(date.getTime())) return new Date("2000-01-01");
    return date;
  }, [defaultValue]);

  const [day, setDay] = useState(format(parsedDate, "dd"));
  const [month, setMonth] = useState(format(parsedDate, "MM"));
  const [year, setYear] = useState(format(parsedDate, "yyyy"));

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return buildRange(1900, currentYear).map((y) => ({
      value: y.toString(),
      name: y.toString(),
    }));
  }, []);

  const daysInMonth = useMemo(() => {
    const monthIndex = parseInt(month, 10) - 1;
    const currentYear = parseInt(year, 10);
    return new Date(currentYear, monthIndex + 1, 0).getDate();
  }, [month, year]);

  const dayOptions = useMemo(
    () =>
      buildRange(1, daysInMonth).map((d) => ({
        value: d.toString().padStart(2, "0"),
        name: d.toString().padStart(2, "0"),
      })),
    [daysInMonth],
  );

  const monthOptions = useMemo(
    () =>
      monthLabels.map((label, idx) => ({
        value: (idx + 1).toString().padStart(2, "0"),
        name: label,
      })),
    [],
  );

  const pickerValue = useMemo(
    () => ({
      day,
      month,
      year,
    }),
    [day, month, year],
  );

  // React synthetic onClick doesn't fire inside IonModal/IonNav subtrees (React
  // 17+ delegates to the React root, but IonModal portals outside it). Use a
  // stable native click listener that reads latest values via a ref instead.
  const latestRef = useRef({ year, month, day, onSelect });
  latestRef.current = { year, month, day, onSelect };
  const confirmRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = confirmRef.current;
    if (!el) return;
    const handler = () => {
      const { year, month, day, onSelect } = latestRef.current;
      onSelect(`${year}-${month}-${day}`);
    };
    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, []);

  const handleSelect = (nextDay: string, nextMonth: string, nextYear: string) => {
    const clampedDay = Math.min(
      parseInt(nextDay, 10),
      new Date(parseInt(nextYear, 10), parseInt(nextMonth, 10), 0).getDate(),
    )
      .toString()
      .padStart(2, "0");
    setDay(clampedDay);
    setMonth(nextMonth);
    setYear(nextYear);
  };

  return (
    <div className="space-y-3 p-9">
      <h3 className={textVariants.title3 + " text-center"}>Birthday</h3>
      <div className="relative">
        <PickerDivider itemHeight={64} />
        <Picker
          value={pickerValue}
          onChange={(val) =>
            handleSelect(val.day as string, val.month as string, val.year as string)
          }
          wheelMode="natural"
          itemHeight={64}
          className="mask-image-none"
          showDivider={false}
        >
          <Picker.Column name="day">
            {dayOptions.map((option) => (
              <Picker.Item key={option.value} value={option.value}>
                {({ selected }) => (
                  <div
                    className={cn(
                      "flex h-16 items-center justify-center text-center !text-black opacity-50",
                      {
                        [textVariants.title1]: !selected,
                        [textVariants.largeTitle]: selected,
                        "font-semibold text-white opacity-100": selected,
                      },
                    )}
                  >
                    {option.name}
                  </div>
                )}
              </Picker.Item>
            ))}
          </Picker.Column>

          <Picker.Column name="month">
            {monthOptions.map((option) => (
              <Picker.Item key={option.value} value={option.value}>
                {({ selected }) => (
                  <div
                    className={cn(
                      "flex h-16 transform items-center justify-center text-center !text-black opacity-50",
                      {
                        [textVariants.title1]: !selected,
                        [textVariants.largeTitle]: selected,
                        "font-semibold text-white opacity-100": selected,
                      },
                    )}
                  >
                    {option.name}
                  </div>
                )}
              </Picker.Item>
            ))}
          </Picker.Column>

          <Picker.Column name="year">
            {yearOptions.map((option) => (
              <Picker.Item key={option.value} value={option.value}>
                {({ selected }) => (
                  <div
                    className={cn(
                      "flex h-16 items-center justify-center text-center !text-black opacity-50",
                      {
                        [textVariants.title1]: !selected,
                        [textVariants.largeTitle]: selected,
                        "font-semibold text-white opacity-100": selected,
                      },
                    )}
                  >
                    {option.name}
                  </div>
                )}
              </Picker.Item>
            ))}
          </Picker.Column>
        </Picker>
      </div>
      <div ref={confirmRef}>
        <FilledButton color="purple" text="Confirm Birthday" />
      </div>
    </div>
  );
});
